import { useCallback, useEffect, useMemo, useRef } from 'react';
import { isSucceeded } from 'reporting-data/asyncData/AsyncData';
import { useAsyncFetcher } from 'reporting-data/asyncData/useAsyncFetcher';
import { isSearchReport } from 'reporting-data/report/configReportGetters';
import { getReportDatasetFromReportResolve } from 'reporting-data/report/reportResolveUtils';
import { isConfigReport } from 'reporting-data/tsTypes/reportTypes';
import { isImmutableJourneyReport } from 'reporting-snowflake/funnel/types/journeyReport';
import { getNonSearchReportInsightsApi, getSearchReportInsightsApi } from '../../../../api/reportingInsightsApi';
import { GENERATE_AI_INSIGHTS_ENDPOINT_V2_GATE } from '../../../../constants/gates';
import { useGate } from '../../../../hooks/useGate';
import { useFetchAsyncReportAIAssistantInsights } from './useFetchAsyncReportAIAssistantInsights';
const DEBOUNCE_DELAY = 500;
export const getIsReportChangedAndFetchNotDeferred = (report, prevReportRef, isDeferred) => {
  /**
   * don't reset the prevReportRef if the request is deferred because if isDeferred is true,
   * useAsyncFetcher does not allow dep updates to trigger new requests. Because of this
   * we only want to mark a report as different to trigger changes only when the hook is not
   * deferred
   *  */
  if (prevReportRef.current !== report && !isDeferred) {
    prevReportRef.current = report;
    return true;
  } else {
    return false;
  }
};
export const resultFormatter = res => {
  const insightVerification = res.get('insightVerification');
  const groupCorrelationId = res.getIn(['generationMetadata', 'insights', '0', 'correlationId']);
  const insightContent = res.getIn(['generationMetadata', 'insightsVerify']).map(content => ({
    value: content.get('values').first(),
    correlationId: content.get('correlationId'),
    verificationResult: insightVerification.get(content.get('values').first())
  }));
  return {
    groupCorrelationId,
    insights: insightContent.toJS()
  };
};

/**
 *
 * @param {Report} report - The report object
 * @param {any} reportResolve - The report resolve object
 * @param {boolean} isDeferred - Whether the request is deferred
 * @param {number} numberOfInsights - The number of insights to fetch
 * @param {boolean} enableSameReportCaching - Whether to enable caching that returns the previous successful result if the report has not changed. If enabled, it will defer fetch of new insights and return cached result until a change has been made to report
 *
 * @returns {AsyncData<ReportingAIAssistantResponse>} - The async data containing the insights
 */
const useFetchReportAIAssistantInsights = ({
  report,
  reportResolve,
  isDeferred = false,
  numberOfInsights = 3,
  enableSameReportCaching
}) => {
  const prevReportRef = useRef();
  const prevInsightFetchResultRef = useRef();
  const isReportChangedAndFetchNotDeferred = getIsReportChangedAndFetchNotDeferred(report, prevReportRef, isDeferred);
  const fetcher = useCallback(param => {
    if (isConfigReport(report) && isSearchReport(report)) {
      return getSearchReportInsightsApi({
        param,
        options: {
          numberOfInsights,
          ttl: DEBOUNCE_DELAY
        }
      });
    }
    return getNonSearchReportInsightsApi({
      param,
      options: {
        numberOfInsights,
        ttl: DEBOUNCE_DELAY
      },
      report
    });
  }, [report, numberOfInsights]);
  const requestArgs = useMemo(() => {
    const dataset = getReportDatasetFromReportResolve(report, reportResolve);
    if (isConfigReport(report) && isSearchReport(report)) {
      return {
        data: Object.assign({}, report.toJS())
      };
    }
    return {
      data: {
        reportEgg: report.toJS(),
        dataset
      }
    };
  }, [report, reportResolve]);
  const shouldDeferRequestBecauseReportIsSame = enableSameReportCaching && !isReportChangedAndFetchNotDeferred && !!prevInsightFetchResultRef.current;
  const reportResolveData = isImmutableJourneyReport(report) ? reportResolve === null || reportResolve === void 0 ? void 0 : reportResolve.get('resolveDatamap') : reportResolve === null || reportResolve === void 0 ? void 0 : reportResolve.data;
  const deferred = shouldDeferRequestBecauseReportIsSame || !reportResolve || !reportResolveData || isDeferred;
  const asyncFetcher = useAsyncFetcher({
    requestArgs,
    fetcher,
    resultFormatter,
    deferred
  });
  useEffect(() => {
    if (isSucceeded(asyncFetcher)) {
      // cache last succeeded result
      prevInsightFetchResultRef.current = asyncFetcher;
    }
    if (isReportChangedAndFetchNotDeferred) {
      // if report changes, clear the cache. This is because the insights are not valid for the current report anymore
      prevInsightFetchResultRef.current = undefined;
    }
  }, [asyncFetcher, isReportChangedAndFetchNotDeferred, prevInsightFetchResultRef]);
  if (enableSameReportCaching && !isReportChangedAndFetchNotDeferred && !!prevInsightFetchResultRef.current) {
    return prevInsightFetchResultRef.current;
  }
  return asyncFetcher;
};
export const useFetchReportAIAssistantInsightsWithV2 = () => {
  const isUngatedForAIInsightsEndpointV2 = useGate(GENERATE_AI_INSIGHTS_ENDPOINT_V2_GATE);
  return isUngatedForAIInsightsEndpointV2 ? useFetchAsyncReportAIAssistantInsights : useFetchReportAIAssistantInsights;
};
export default useFetchReportAIAssistantInsights;